import React, { useEffect, useMemo } from "react";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/types/entity";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { LoadingSpinner, Modal } from "@mapmycustomers/ui";

import CreateEmailContext from "@app/component/email/creation/CreateEmailContext";
import createEmailAssociatedEntity from "@app/util/email/createEmailAssociatedEntity";

import messages from "../messages";
import Preview from "../Preview";
import RecipientSelector from "../RecipientSelector";

import styles from "./PreviewModal.module.scss";

const PreviewModal: React.FC = () => {
  const intl = useIntl();

  const {
    body,
    createPreview,
    hidePreview,
    preview,
    previewLoading,
    previewOpen,
    previewRecipientId,
    recipients,
    setPreviewRecipientId,
    subject,
  } = useNonNullableContext(CreateEmailContext);

  const selectedRecipient = useMemo(
    () => recipients.find((recipient) => recipient.id === previewRecipientId),
    [recipients, previewRecipientId]
  );

  useEffect(() => {
    if (previewOpen && previewRecipientId) {
      createPreview({
        associatedEntities: [createEmailAssociatedEntity({ entityId: previewRecipientId })],
        body: body,
        entityType: recipients[0]?.entity ?? EntityType.COMPANY,
        isHtml: true,
        logActivity: false,
        subject: subject,
        tracking: true,
      });
    }
  }, [body, createPreview, recipients, previewOpen, previewRecipientId, subject]);

  return (
    <Modal
      className={styles.modal}
      closable
      destroyOnClose
      footer={null}
      onCancel={hidePreview}
      open={previewOpen}
      title={intl.formatMessage(messages.previewAs)}
      width="48rem"
    >
      <div className={styles.preview}>
        {preview && selectedRecipient ? (
          <Preview preview={preview} recipient={selectedRecipient} />
        ) : (
          <div className={styles.noPreview}>
            {previewLoading && <LoadingSpinner />}
            <span className={styles.noPreviewTitle}>
              {intl.formatMessage(messages.previewWillShowHere)}
            </span>
            <span className={styles.noPreviewDescription}>
              {intl.formatMessage(messages.chooseRecipient)}
            </span>
          </div>
        )}
      </div>
      <div className={styles.selectorContainer}>
        <div className={styles.previewAs}>{intl.formatMessage(messages.previewAs)}</div>
        <RecipientSelector
          disabled={previewLoading}
          onSelectRecipientId={setPreviewRecipientId}
          recipients={recipients}
          selectedRecipientId={previewRecipientId}
        />
        <div className={styles.previewAs}>{intl.formatMessage(messages.previewAsDescription)}</div>
      </div>
    </Modal>
  );
};

export default PreviewModal;
